/**
 * Le champ d'écriture (docs/13 §4).
 *
 * Il est **désactivé et expliqué** quand `can_post` est faux : laisser
 * l'utilisateur écrire trois paragraphes pour recevoir un 409 serait exact et
 * insupportable. C'est la seule raison d'être de ce champ dans la réponse du
 * serveur — le client ne rejoue pas RM-07, il affiche le verdict.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/ui/Button";

interface MessageComposerProps {
  canPost: boolean;
  isSending: boolean;
  onSend: (body: string) => void;
}

export function MessageComposer({ canPost, isSending, onSend }: MessageComposerProps) {
  const { t } = useTranslation("messaging");
  const [draft, setDraft] = useState("");
  const trimmed = draft.trim();

  if (!canPost) {
    return (
      <p
        className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100"
        data-testid="composer-closed"
      >
        {t("composer.closed")}
      </p>
    );
  }

  return (
    <form
      className="flex items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (trimmed === "") {
          return;
        }
        onSend(trimmed);
        setDraft("");
      }}
    >
      <textarea
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        rows={2}
        maxLength={4000}
        aria-label={t("composer.label")}
        placeholder={t("composer.placeholder")}
        className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
      />
      {/* Désactivé sur le brouillon **taillé** : le serveur refuse un message
          blanc (422), et proposer un bouton qui ne peut qu'échouer serait une
          promesse fausse. */}
      <Button type="submit" disabled={trimmed === "" || isSending}>
        {t("composer.send")}
      </Button>
    </form>
  );
}
